import { KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { createMask } from '@ngneat/input-mask';
import { ComplianceSettingForm } from 'src/app/classes/form-groups/compliance-setting-form';
import { Company } from 'src/app/classes/models';
import { ComplianceSettingLiabilityType } from 'src/app/interfaces/compliance-setting-liability-types';
import { UtilityService } from 'src/app/services/utility.service';

type ProfessionalLiabilityMap = Map<
  string,
  {
    controlName: string;
    show: boolean;
    inputType: 'Select' | 'Checkbox';
    selectOptions?: any;
    checkBoxLabel?: 'Waive' | 'Required?' | 'Allowed?';
  }
>;

@Component({
  selector: 'app-compliance-professional-liability',
  templateUrl: './compliance-professional-liability.component.html',
  styleUrls: ['./compliance-professional-liability.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ComplianceProfessionalLiabilityComponent {
  constructor(private UtilityService: UtilityService) {}

  @Input() complianceSettingForms?: ComplianceSettingForm[];
  @Input() company?: Company;
  @Input() amBestValueMap?: Map<string, string | number | null>;
  @Input() amBestFinancialValueMap?: Map<
    string,
    { value: string | null; hint: string }
  >;
  @Input() amountPerOccurrenceMap?: Map<string, number | null>;
  @Input() amountAggregateMap?: Map<string, number | null>;

  @Output() updateRequiredFields =
    new EventEmitter<ComplianceSettingLiabilityType>();

  checkBoxFields?: Map<string, { controlName: string; show: boolean }>;
  professionalLiabilityMap?: ProfessionalLiabilityMap;

  collapse: boolean = true;

  currencyMask = createMask({
    alias: 'numeric',
    groupSeparator: ',',
    placeholder: '',
    rightAlign: false
  });

  // The currency mask parser doesn't work. So, we'll handle it ourselves.
  applyCurrencyFormat(FormControl: FormControl, value: any) {
    FormControl.patchValue(
      value === null ? null : parseInt(value.replace(/[^\d]/g, ''), 10),
      { emitViewToModelChange: false }
    );
  }

  keyValuePairDefaultOrder(
    a: KeyValue<any, any>,
    b: KeyValue<any, any>
  ): number {
    return 0;
  }

  getOptionValue(option: any): any {
    return option.value && typeof option.value === 'object'
      ? option.value.value
      : option.value;
  }

  onProfessionalLiabilityIsRequiredChange() {
    this.updateRequiredFields.emit('professionalLiability');
  }

  loadProfessionalLiabilityFields() {
    this.professionalLiabilityMap = new Map([
      [
        'Policy Required?',
        {
          controlName: 'errorsAndOmissions_isRequired',
          inputType: 'Checkbox',
          checkBoxLabel: 'Required?',
          show: true
        }
      ],
      [
        'A.M. Best Rating',
        {
          controlName: 'errorsAndOmissions_amBestRating',
          inputType: 'Select',
          selectOptions: this.amBestValueMap,
          show: true
        }
      ],
      [
        'Waive Non-Rated A.M. Best Rating Carriers?',
        {
          controlName: 'pl_AllowNRRatedAMBestCarriers',
          inputType: 'Checkbox',
          checkBoxLabel: 'Waive',
          show: true
        }
      ],
      [
        'A.M. Best Financial Size',
        {
          controlName: 'errorsAndOmissions_financialSizeCategory',
          inputType: 'Select',
          selectOptions: this.amBestFinancialValueMap,
          show: true
        }
      ],
      [
        'Each Occurrence',
        {
          controlName: 'errorsAndOmissions_policyAmount',
          inputType: 'Select',
          selectOptions: this.amountAggregateMap,
          show: true
        }
      ],
      [
        'Additional Insured',
        {
          controlName: 'errIsNamed',
          inputType: 'Checkbox',
          checkBoxLabel: 'Required?',
          show: true
        }
      ],
      [
        'Waiver of Subrogation',
        {
          controlName: 'errIsAgency',
          inputType: 'Checkbox',
          checkBoxLabel: 'Required?',
          show: true
        }
      ],
      [
        'Valid Description of Operations',
        {
          controlName: 'errDescOps',
          inputType: 'Checkbox',
          checkBoxLabel: 'Required?',
          show: true
        }
      ],
      [
        'COI Name/Legal Name Mismatch',
        {
          controlName: 'professionalLiability_legalNameMismatch',
          inputType: 'Checkbox',
          checkBoxLabel: 'Required?',
          show: true
        }
      ]
    ]);
  }

  ngOnInit(): void {
    // this.loadProfessionalLiabilityFields();

    this.updateRequiredFields.emit('professionalLiability');

    this.checkBoxFields = new Map([
      ['Additional Insured', { controlName: 'errIsNamed', show: true }],
      ['Waiver of Subrogation', { controlName: 'errIsAgency', show: true }],
      [
        'Valid Description of Operations',
        { controlName: 'errDescOps', show: true }
      ],
      [
        'COI Name/Legal Name Mismatch',
        { controlName: 'professionalLiability_legalNameMismatch', show: true }
      ]
    ]);
  }
}
